import { decodeLog } from '@coderaw/log'
import { defaultEnv } from '@coderaw/runtime'
import { createReplayPlayer, type ReplayPlayer } from '../components/replay/ReplayPlayer'
import { formatReal } from '../components/replay/replayState'
import { ApiError, type VersionDetail } from '../lib/api'
import { dayLight } from '../lib/clock'
import { el, replaceChildren } from '../lib/dom'
import type { Page, PageContext } from './types'

const header = (v: VersionDetail): HTMLElement =>
  el(
    'header',
    { class: 'replay-header' },
    v.avatarUrl
      ? el('img', { class: 'avatar', src: v.avatarUrl, alt: '', width: 40, height: 40 })
      : null,
    el(
      'div',
      {},
      el('h1', { class: 'replay-title' }, `~${v.handle}`),
      el(
        'p',
        { class: 'prompt-sub' },
        `${v.prompt} · ${v.tokens} tokens · typed over ${formatReal(v.realMs)} in ${v.opCount} keystrokes`,
      ),
    ),
  )

const codePanel = (code: string): HTMLElement =>
  el(
    'details',
    { class: 'panel' },
    el('summary', {}, 'cat plot.js'),
    el('pre', { class: 'example-code' }, el('code', {}, code)),
  )

const failure = (error: unknown): string => {
  if (error instanceof ApiError && error.status === 404) return 'No plot here. The road just ends.'
  return 'Could not load this replay. Try again in a moment.'
}

const ownerStats = async (ctx: PageContext, v: VersionDetail): Promise<HTMLElement | null> => {
  if (!ctx.me || ctx.me.handle !== v.handle) return null
  try {
    const { views } = await ctx.api.stats(v.id)
    const times = views === 1 ? 'time' : 'times'
    return el('p', { class: 'panel-note' }, `watched ${views} ${times}. Only you see this.`)
  } catch {
    return null
  }
}

/** One replay, loaded by id. Watching it counts as a view for the owner's private tally. */
export const createReplayPage = (ctx: PageContext, id: string): Page => {
  const element = el(
    'article',
    { class: 'page page-replay', 'aria-busy': 'true' },
    el('p', { class: 'empty' }, 'walking over…'),
  )
  const statsSlot = el('div', { class: 'stats-slot' })
  let player: ReplayPlayer | null = null
  // Navigating away mid-load must not leave a player ticking in the background.
  let destroyed = false
  const env = () =>
    defaultEnv({ weather: ctx.prompt.weather, intensity: ctx.prompt.intensity, light: dayLight() })

  const load = async (): Promise<void> => {
    try {
      const [version, bytes] = await Promise.all([ctx.api.getVersion(id), ctx.api.getLog(id)])
      const log = await decodeLog(bytes)
      if (destroyed) return
      void ctx.api.recordView(id).catch(() => undefined)
      player = createReplayPlayer({ log, plotId: version.id, env })
      replaceChildren(element, [
        header(version),
        player.element,
        statsSlot,
        codePanel(version.code),
      ])
      const stats = await ownerStats(ctx, version)
      if (!destroyed && stats) replaceChildren(statsSlot, [stats])
    } catch (error) {
      if (!destroyed) replaceChildren(element, [el('p', { class: 'empty' }, failure(error))])
    } finally {
      element.removeAttribute('aria-busy')
    }
  }
  void load()

  return {
    element,
    destroy: () => {
      destroyed = true
      player?.destroy()
      player = null
    },
  }
}
